/**
 * Lo schema dentro un `.flw`/`.txt` (CF12, D14) — CL07.
 *
 * ## Il formato, e perche' questo
 *
 * Un `.flw` resta **un solo documento XML**: lo schema viaggia dentro
 * `<bpmn:extensionElements>` del processo, in un elemento del namespace
 * Flowenti:
 *
 * ```xml
 * <bpmn:process id="Process_1">
 *   <bpmn:extensionElements>
 *     <flowenti:customFields
 *         xmlns:flowenti="http://flowenti.com/schema/custom-fields"
 *         contractVersion="1" schemaRevision="3" policyRevision="2">
 *       {"contractVersion":1,"modelId":"…","fields":[…]}
 *     </flowenti:customFields>
 *   </bpmn:extensionElements>
 * ```
 *
 * Tre ragioni, e valgono tutte e tre:
 *
 * - **Niente secondo file.** Il piano parla di `.flw` e `.txt`; un contenitore
 *   nuovo (archivio, JSON affiancato) romperebbe chi apre il file con un
 *   editor BPMN, che oggi funziona.
 * - **Un lettore vecchio non rompe.** Chi non conosce l'elemento lo ignora:
 *   importa il modello come ha sempre fatto, senza lo schema. Il difetto e'
 *   la perdita silenziosa dei campi, ed e' per questo che l'import **mostra**
 *   cosa il file portava prima di salvarlo.
 * - **Un solo DTO.** Il testo e' il `SchemaDocument` del contratto, non una
 *   seconda forma da mantenere allineata: le due divergono sempre, e a
 *   divergere sarebbe la cosa che si importa.
 *
 * Il namespace e' dichiarato sull'elemento stesso: il documento importato non
 * deve conoscere i namespace del file che l'ha esportato.
 *
 * ## Cosa non fa
 *
 * Non riassocia niente. Gli id dei registri **non** si rimappano qui: un
 * riferimento che questa organizzazione non ha si **segnala** (vedi
 * `unresolvableRegistries`), perche' riassociarlo a un registro che assomiglia
 * sarebbe indovinare quale contatore l'utente intendeva — e un contatore
 * sbagliato e' un numero emesso che nessuno puo' piu' togliere.
 */

import {
  FIELD_TYPES,
  type FieldDefinition,
  type FieldType,
  type SchemaDocument,
} from "model/CustomFields";

/** Il namespace dei campi personalizzati dentro il BPMN. */
export const SCHEMA_NS = "http://flowenti.com/schema/custom-fields";
/** Il nome qualificato dell'elemento. */
export const SCHEMA_TAG = "flowenti:customFields";
const SCHEMA_LOCAL = "customFields";

/**
 * Cosa porta un file.
 *
 * `none` e `invalid` sono cose diverse e non si confondono: un `.flw` che non
 * parla di campi personalizzati e' un file legittimo, un elemento che c'e' e
 * non si legge e' un file da **segnalare** — importarlo in silenzio
 * cancellerebbe i campi che diceva di portare.
 */
export type SchemaRead =
  | { kind: "none" }
  | { kind: "ok"; document: SchemaDocument }
  | { kind: "invalid"; reason: "xml" | "json" | "shape" };

const findElement = (
  radice: Document | Element,
  localName: string
): Element | null => {
  const tutti = Array.from(radice.getElementsByTagName("*"));
  return tutti.find((el) => el.localName === localName) ?? null;
};

/** Il JSON che il file porta, se e' un documento di schema. */
function asSchemaDocument(valore: unknown): SchemaDocument | null {
  if (!valore || typeof valore !== "object") return null;
  const documento = valore as Partial<SchemaDocument>;
  if (documento.contractVersion !== 1) return null;
  if (!Array.isArray(documento.fields)) return null;
  if (typeof documento.schemaRevision !== "number") return null;
  return documento as SchemaDocument;
}

/**
 * Legge lo schema da un `.flw`/`.txt`.
 *
 * Richiede un `DOMParser`: e' del browser (e di jsdom nei test), e un parser
 * XML scritto a mano qui sarebbe la cosa che sbaglia sul primo file che non
 * abbiamo generato noi.
 */
export function readSchema(xml: string): SchemaRead {
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  // Un XML malformato non produce un'eccezione: produce un documento con un
  // `parsererror` dentro, che senza controllo passerebbe per "nessuno schema".
  if (doc.getElementsByTagName("parsererror").length > 0) {
    return { kind: "invalid", reason: "xml" };
  }

  const elemento = findElement(doc, SCHEMA_LOCAL);
  if (!elemento) return { kind: "none" };

  let parsed: unknown;
  try {
    parsed = JSON.parse(elemento.textContent ?? "");
  } catch {
    return { kind: "invalid", reason: "json" };
  }

  const documento = asSchemaDocument(parsed);
  return documento ? { kind: "ok", document: documento } : { kind: "invalid", reason: "shape" };
}

/**
 * Scrive lo schema nel processo, sostituendo quello che c'era.
 *
 * L'elemento va dentro `extensionElements`, che in BPMN e' il primo figlio del
 * processo: un elemento fuori posto lo perderebbe un lettore conforme, e il
 * file sembrerebbe valido.
 */
export function embedSchema(xml: string, document: SchemaDocument): string {
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  const processo = findElement(doc, "process");
  if (!processo) throw new Error("Nessun processo BPMN nel file da esportare");

  let contenitore = Array.from(processo.children).find(
    (el) => el.localName === "extensionElements"
  );
  if (!contenitore) {
    contenitore = doc.createElementNS(
      processo.namespaceURI ?? "http://www.omg.org/spec/BPMN/20100524/MODEL",
      "bpmn:extensionElements"
    );
    processo.insertBefore(contenitore, processo.firstChild);
  }

  // Sostituire, non accumulare: due elementi di schema nello stesso file
  // farebbero decidere al lettore quale dei due credere.
  Array.from(contenitore.children)
    .filter((el) => el.localName === SCHEMA_LOCAL)
    .forEach((el) => contenitore?.removeChild(el));

  const nuovo = doc.createElementNS(SCHEMA_NS, SCHEMA_TAG);
  nuovo.setAttribute("contractVersion", String(document.contractVersion));
  nuovo.setAttribute("schemaRevision", String(document.schemaRevision));
  nuovo.setAttribute("policyRevision", String(document.policyRevision));
  // `textContent` fa l'escaping da solo: il JSON con `<` in una label resta
  // testo, e torna identico rileggendolo.
  nuovo.textContent = JSON.stringify(document);
  contenitore.appendChild(nuovo);

  const serializzato = new XMLSerializer().serializeToString(doc);
  // `XMLSerializer` non emette la dichiarazione, e il file deve restare
  // riapribile da chi l'ha scritto: un parser severo la pretende, e il file
  // verrebbe rifiutato per una riga che c'era prima.
  return serializzato.startsWith("<?xml")
    ? serializzato
    : `<?xml version="1.0" encoding="UTF-8"?>\n${serializzato}`;
}

/** I tipi che il contratto conosce, per non accogliere un file che ne inventa uno. */
const TIPI_NOTI = new Set<string>(FIELD_TYPES);

/** Un difetto del documento trasportato: un codice, e il campo che lo riguarda. */
export interface TransportIssue {
  code: "contract_version" | "duplicate_id" | "unknown_type" | "missing_label";
  /** Assente per i difetti che riguardano il documento intero. */
  fieldId?: string;
}

/**
 * I problemi **strutturali** del documento trasportato.
 *
 * Sono codici, non frasi: la frase e' nel catalogo, e comporla qui sarebbe una
 * seconda traduzione. Si guarda solo cio' che rende il documento inapplicabile:
 * versione del contratto, id ripetuti (i valori non sarebbero piu' assegnabili
 * a un campo), tipi sconosciuti, campi senza etichetta (non mostrabili).
 *
 * Sono **bloccanti**, ed e' una distinzione voluta: i registri non risolvibili
 * si possono accettare sapendo cosa si perde, un campo con un tipo che non
 * esiste no — quello si importa rotto e non si accorge nessuno.
 */
export function transportIssues(document: SchemaDocument): TransportIssue[] {
  const issues: TransportIssue[] = [];
  if (document.contractVersion !== 1) issues.push({ code: "contract_version" });

  const visti = new Set<string>();
  for (const campo of document.fields) {
    if (visti.has(campo.fieldId)) {
      issues.push({ code: "duplicate_id", fieldId: campo.fieldId });
    }
    visti.add(campo.fieldId);

    if (!TIPI_NOTI.has(campo.type as FieldType)) {
      issues.push({ code: "unknown_type", fieldId: campo.fieldId });
    }
    if (!campo.label || campo.label.trim() === "") {
      issues.push({ code: "missing_label", fieldId: campo.fieldId });
    }
  }
  return issues;
}

/**
 * I campi che puntano a un registro che questa organizzazione non ha.
 *
 * Si **segnalano** e non si rimappano: associare un riferimento sconosciuto al
 * registro piu' simile significherebbe emettere numeri da un contatore che
 * l'utente non ha scelto.
 */
export function unresolvableRegistries(
  document: SchemaDocument,
  availableRegistryIds: readonly string[]
): { fieldId: string; label: string; registryId: string }[] {
  const noti = new Set(availableRegistryIds);
  const mancanti: { fieldId: string; label: string; registryId: string }[] = [];

  for (const campo of document.fields as FieldDefinition[]) {
    const registryId = campo.config?.registryId;
    if (typeof registryId !== "string" || registryId === "") continue;
    if (!noti.has(registryId)) {
      mancanti.push({
        fieldId: campo.fieldId,
        label: campo.label,
        registryId,
      });
    }
  }
  return mancanti;
}
